import { useEffect, useRef, useSyncExternalStore } from 'react'
import { z } from 'zod'
import { GroceryId, type GroceryItem } from '@hewey/shared'
import { api, ApiError } from '@api'

// Ticks waiting to reach the server, by item id. Every tick goes through
// here, online or not: it's held in localStorage until a request gets
// through, so ticking still works with no signal in a shop. Ticks only —
// adding and editing still need a connection.
const STORAGE_KEY = 'hewey.groceries.ticks'
const RETRY_MS = 5_000

const Pending = z.record(GroceryId, z.boolean())
type Pending = Partial<Record<GroceryId, boolean>>

export type TickQueue = {
	pending: Pending
	// A send failed for a reason worth retrying, and ticks are still held.
	stalled: boolean
}

const load = (): Pending => {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY)
		if (raw === null) return {}
		const parsed = Pending.safeParse(JSON.parse(raw))
		return parsed.success ? parsed.data : {}
	} catch {
		return {}
	}
}

let state: TickQueue = { pending: load(), stalled: false }
const listeners = new Set<() => void>()
const sentListeners = new Set<(item: GroceryItem) => void>()

const update = (next: Partial<TickQueue>) => {
	state = { ...state, ...next }
	try {
		if (Object.keys(state.pending).length === 0) {
			window.localStorage.removeItem(STORAGE_KEY)
		} else {
			window.localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify(state.pending),
			)
		}
	} catch {
		// Storage full or disabled — the tick still sends from memory.
	}
	for (const l of listeners) l()
}

// Clear the entry only if it still holds what was sent: a tick made while
// the request was out is newer, and still needs sending.
const settle = (id: GroceryId, ticked: boolean) => {
	if (state.pending[id] !== ticked) return
	const { [id]: _, ...rest } = state.pending
	const empty = Object.keys(rest).length === 0
	update({ pending: rest, stalled: empty ? false : state.stalled })
}

// 4xx means the item is gone or isn't ours, and resending won't change that.
// Anything else (no response, 5xx, throttling) is worth another go.
const retryable = (e: unknown): boolean =>
	!(e instanceof ApiError) ||
	e.status >= 500 ||
	e.status === 408 ||
	e.status === 429

const run = async () => {
	if (!navigator.onLine) return
	// Keyed on id and value, so a tick changed mid-send is sent again.
	const tried = new Set<string>()
	for (;;) {
		const next = (
			Object.entries(state.pending) as [GroceryId, boolean][]
		).find(([id, ticked]) => !tried.has(`${id}:${ticked}`))
		if (!next) return
		const [id, ticked] = next
		tried.add(`${id}:${ticked}`)
		try {
			// One at a time: a send that fails stops the rest until the
			// next attempt.
			// eslint-disable-next-line no-await-in-loop
			const res = await api.grocery.update(id, { ticked })
			settle(id, ticked)
			if (state.stalled) update({ stalled: false })
			for (const l of sentListeners) l(res.item)
		} catch (e) {
			if (!retryable(e)) {
				settle(id, ticked)
				continue
			}
			update({ stalled: true })
			return
		}
	}
}

let flushing: Promise<void> | null = null

export const flushTicks = (): Promise<void> => {
	flushing ??= run().finally(() => {
		flushing = null
	})
	return flushing
}

export const queueTick = (id: GroceryId, ticked: boolean) => {
	update({ pending: { ...state.pending, [id]: ticked } })
	void flushTicks()
}

// Forget a tick for an item that's been deleted.
export const dropTicks = (ids: GroceryId[]) => {
	const rest = { ...state.pending }
	for (const id of ids) delete rest[id]
	update({ pending: rest })
}

const subscribe = (onChange: () => void) => {
	listeners.add(onChange)
	return () => listeners.delete(onChange)
}

// The held ticks, for laying over the server's list. Retries on mount, on
// reconnect, on return to the tab, and every few seconds while any are
// held. `onSent` gets the server's copy of each item once its tick lands.
export const useTickQueue = (
	onSent: (item: GroceryItem) => void,
): TickQueue => {
	const queue = useSyncExternalStore(subscribe, () => state)

	const onSentRef = useRef(onSent)
	onSentRef.current = onSent

	useEffect(() => {
		const sent = (item: GroceryItem) => onSentRef.current(item)
		sentListeners.add(sent)

		const retry = () => {
			if (document.visibilityState === 'visible') void flushTicks()
		}
		retry()
		window.addEventListener('online', retry)
		document.addEventListener('visibilitychange', retry)
		const interval = window.setInterval(() => {
			if (Object.keys(state.pending).length > 0) retry()
		}, RETRY_MS)

		return () => {
			sentListeners.delete(sent)
			window.removeEventListener('online', retry)
			document.removeEventListener('visibilitychange', retry)
			window.clearInterval(interval)
		}
	}, [])

	return queue
}
